// src/features/captions/useCaptionTerms.ts

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { CAPTION_GENERATE_CONSTRAINTS, CAPTIONS_QUERY_KEYS } from '@/lib/constants/captions';
import { CaptionsApi } from './CaptionsApi';
import { useCaptionsEligibility } from './useCaptionsEligibility';
import type { CaptionTermSetData } from './types';

/** All saved term sets (account scope + media-stream scopes) for the selected account. */
export function useCaptionTermSets() {
  const { selectedCId } = useAuth();
  const { eligible } = useCaptionsEligibility();

  const query = useQuery({
    queryKey: CAPTIONS_QUERY_KEYS.termSets(selectedCId ?? 0),
    queryFn: async () => {
      const res = await CaptionsApi.getTermSets(selectedCId!);
      return res.term_sets;
    },
    enabled: selectedCId != null && eligible,
    staleTime: 60 * 1000,
  });

  const termSets: CaptionTermSetData[] = query.data ?? [];

  return {
    termSets,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Recent AI research runs (last 10, newest first per server). Fetched lazily —
 * pass enabled=false until the generate modal actually opens.
 *
 * pollWhilePending: the api ALB (60 s idle timeout) drops the held generate
 * response on every real run, so while a submitted run is unresolved this
 * query IS the outcome channel — it polls on an interval and stays enabled
 * even with the modal closed (the flow must survive closing it).
 */
export function useCaptionGenerations(enabled: boolean, pollWhilePending = false) {
  const { selectedCId } = useAuth();
  const { eligible } = useCaptionsEligibility();

  const query = useQuery({
    queryKey: CAPTIONS_QUERY_KEYS.generations(selectedCId ?? 0),
    queryFn: async () => {
      const res = await CaptionsApi.getGenerations(selectedCId!);
      return res.generations;
    },
    enabled: (enabled || pollWhilePending) && selectedCId != null && eligible,
    staleTime: 60 * 1000,
    refetchInterval: pollWhilePending ? CAPTION_GENERATE_CONSTRAINTS.GENERATION_POLL_MS : undefined,
  });

  return {
    generations: query.data ?? [],
    isLoading: query.isLoading,
    /** Bumps on every completed poll tick even when the list is unchanged (structural sharing keeps the array reference stable). */
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
